import React, { useState } from 'react';
import { ArrowTrendingUpIcon, CurrencyDollarIcon, BriefcaseIcon, CalculatorIcon, CreditCardIcon } from '@heroicons/react/24/outline';

const HistoryTab = ({ register, errors, watch, setValue, inputClassName, history }) => {
  const [activeHistoryTab, setActiveHistoryTab] = useState('jobs');

  // Mock data for history tables
  const mockJobs = [
    { id: 1, number: 'JOB-001', title: 'Kitchen Sink Repair', date: '2024-01-15', status: 'Completed', amount: 150.00 },
    { id: 2, number: 'JOB-002', title: 'Bathroom Faucet Installation', date: '2024-01-20', status: 'Completed', amount: 200.00 },
    { id: 3, number: 'JOB-003', title: 'Water Heater Maintenance', date: '2024-02-01', status: 'In Progress', amount: 300.00 }
  ];

  const mockEstimates = [
    { id: 1, number: 'EST-001', title: 'Kitchen Remodel', date: '2024-01-10', status: 'Approved', amount: 5000.00 },
    { id: 2, number: 'EST-002', title: 'Bathroom Upgrade', date: '2024-01-25', status: 'Pending', amount: 3500.00 },
    { id: 3, number: 'EST-003', title: 'Plumbing System Update', date: '2024-02-05', status: 'Draft', amount: 2500.00 }
  ];

  const mockInvoices = [
    { id: 1, number: 'INV-001', title: 'Kitchen Sink Repair', date: '2024-01-15', status: 'Paid', amount: 150.00 },
    { id: 2, number: 'INV-002', title: 'Bathroom Faucet Installation', date: '2024-01-20', status: 'Paid', amount: 200.00 },
    { id: 3, number: 'INV-003', title: 'Water Heater Maintenance', date: '2024-02-01', status: 'Pending', amount: 300.00 }
  ];

  const mockParentInvoices = [
    { id: 1, number: 'PINV-001', title: 'Monthly Service', date: '2024-01-31', status: 'Paid', amount: 500.00 },
    { id: 2, number: 'PINV-002', title: 'Quarterly Maintenance', date: '2024-02-28', status: 'Pending', amount: 750.00 }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'paid':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in progress':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderHistoryTable = (data) => {
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {item.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(item.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`}>
                    {item.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(item.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Revenue Widgets */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Last 12 Months</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(history.last12Months)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Lifetime Revenue</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(history.lifetimeRevenue)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BriefcaseIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Jobs</p>
                <p className="text-lg font-semibold text-gray-900">{history.totalJobs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalculatorIcon className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">AOV</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(history.aov)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCardIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Current Balance</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(history.currentBalance)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer History */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Customer History</h3>
        
        {/* History Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            <button
             type="button"
              onClick={() => setActiveHistoryTab('jobs')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeHistoryTab === 'jobs'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Jobs
            </button>
            <button
             type="button"
              onClick={() => setActiveHistoryTab('estimates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeHistoryTab === 'estimates'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Estimates
            </button>
            <button
             type="button"
              onClick={() => setActiveHistoryTab('invoices')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeHistoryTab === 'invoices'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Invoices
            </button>
            <button
             type="button"
              onClick={() => setActiveHistoryTab('parent-invoices')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeHistoryTab === 'parent-invoices'
                  ? 'border-[#009efb] text-[#009efb]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Parent Account Invoices
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeHistoryTab === 'jobs' && renderHistoryTable(mockJobs)}
          {activeHistoryTab === 'estimates' && renderHistoryTable(mockEstimates)}
          {activeHistoryTab === 'invoices' && renderHistoryTable(mockInvoices)}
          {activeHistoryTab === 'parent-invoices' && renderHistoryTable(mockParentInvoices)}
        </div>
      </div>
    </div>
  );
};

export default HistoryTab; 